import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import { EntityTypeSupportingDataView, MapEntity } from "@mapmycustomers/shared/types/entity";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { LoadingSpinner } from "@mapmycustomers/ui";

import DataViewButton from "@app/component/DataView/DataViewButton";
import DataViewEntityTypeCount from "@app/component/DataView/type/DataViewEntityTypeCount";
import {
  monoEntitiesCountTextGetterFactory,
  monoEntitiesPaginationTextGetterFactory,
  TextGetters,
} from "@app/component/RecordList";
import useMapView from "@app/component/view/utils/useMapView";
import DataViewWrapper from "@app/scene/map/components/DataViewWrapper";
import EntityRecordView from "@app/scene/map/components/MapMode/components/Sidebar/components/EntityRecordView";
import SelectedPinsSidebar from "@app/scene/map/components/SelectedPinsSidebar";
import ExitLassoModeButton from "@app/scene/map/components/sidebar/ExitLassoModeButton";
import MultiPinBackButton from "@app/scene/map/components/sidebar/MultiPinBackButton";
import { RecordList } from "@app/scene/map/components/sidebar/RecordList";
import BaseSidebar from "@app/scene/map/components/sidebar/Sidebar";
import {
  exitGroupMode,
  exitLassoMode,
  fetchDataViewEntities,
  fetchGroupRecords,
} from "@app/scene/map/store/actions";
import {
  areGroupRecordsLoading,
  getDataViewEntities,
  getGroup,
  getGroupEntityType,
  getGroupRecords,
  getGroupRecordsTotalCount,
  isDataViewLoading,
} from "@app/scene/map/store/groupMode/selectors";
import { getMapViewTool, getMultiPin } from "@app/scene/map/store/mapMode/selectors";
import { getRecordsListTotalBeforeFilter, isLassoLoading } from "@app/scene/map/store/selectors";
import { isEntityViewVisible } from "@app/store/entityView";
import { RootState } from "@app/store/rootReducer";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { mapEntityIdGetter } from "@app/util/map/idGetters";

import Frequency from "./component/Frequency";
import GroupMultiPinRecordList from "./component/GroupMultiPinRecordList";
import GroupProps from "./component/GroupProps";
import GroupRecord from "./component/GroupRecord";
import Title from "./component/Title";

const messages = defineMessages({
  backToLocation: {
    id: "map.recordsList.groupMultiPin.backToLocation",
    defaultMessage: "Back to Location",
    description: "Records list for multi-pin - Back to Location",
  },
  placeholder: {
    id: "map.recordsList.search.groupPlaceholder",
    defaultMessage: "Search records in this group",
    description: "Group records list - search placeholder",
  },
});

type SidebarProps = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

const Sidebar: React.FC<SidebarProps> = ({
  dataViewEntities,
  dataViewLoading,
  entityType,
  group,
  isEntityViewVisible,
  lassoLoading,
  loading,
  multiPin,
  onExitGroupMode,
  onExitLassoMode,
  onFetchDataViewEntities,
  onFetchRecords,
  records,
  recordsCount,
  tool,
}) => {
  const intl = useIntl();
  const [dataViewVisible, showDataView, hideDataView] = useBoolean();

  const {
    viewState: { range },
  } = useMapView();

  const isLassoMode = tool === MapTool.LASSO;
  const isMultiPinMode = !!multiPin;

  const handleRangeChange = useCallback((range) => onFetchRecords({ range }), [onFetchRecords]);
  const handleSearch = useCallback((search) => onFetchRecords({ search }), [onFetchRecords]);

  useEffect(() => {
    // initialize
    if (entityType && !isMultiPinMode) {
      const nameField = getFieldModelByEntityType(entityType).getByName("name");
      const sort = nameField ? [{ field: nameField, order: SortOrder.ASC }] : [];
      onFetchRecords({ sort });
    }
  }, [entityType, onFetchRecords, group?.id, isMultiPinMode]);

  const handleClose = useCallback(() => {
    if (isLassoMode) {
      onExitLassoMode();
    } else {
      onExitGroupMode?.();
    }
  }, [onExitLassoMode, isLassoMode, onExitGroupMode]);

  const textGetters: TextGetters = useMemo(
    () => ({
      countTextGetter: monoEntitiesCountTextGetterFactory(intl, entityType),
      paginationTextGetter: monoEntitiesPaginationTextGetterFactory(intl, entityType),
    }),
    [intl, entityType]
  );

  const handleDataViewClick = useCallback(() => {
    onFetchDataViewEntities();
  }, [onFetchDataViewEntities]);

  useEffect(() => {
    if (!dataViewLoading && dataViewEntities.length > 0) {
      showDataView();
    }
  }, [dataViewEntities, dataViewLoading, showDataView]);

  const entityTypeRecordsCount: DataViewEntityTypeCount = useMemo(() => {
    return { [entityType as EntityTypeSupportingDataView]: dataViewEntities.length };
  }, [entityType, dataViewEntities]);

  if (!entityType) {
    return null;
  }

  return isLassoMode ? (
    <SelectedPinsSidebar
      beforeTitle={<ExitLassoModeButton />}
      itemComponent={GroupRecord}
      loading={loading}
      onFetchRecords={onFetchRecords}
      onRangeChange={handleRangeChange}
      onSearch={handleSearch}
      range={range}
      records={records}
      recordsCount={recordsCount}
      showDataView={false}
    />
  ) : (
    <BaseSidebar
      beforeTitle={<MultiPinBackButton />}
      onClose={handleClose}
      title={<Title />}
      visible
      wrapper={isEntityViewVisible}
    >
      <EntityRecordView
        backButtonLabel={isMultiPinMode ? intl.formatMessage(messages.backToLocation) : undefined}
        onClose={onExitGroupMode}
      />
      {!isEntityViewVisible && (
        <>
          {group ? (
            <>
              {isMultiPinMode ? (
                <GroupMultiPinRecordList />
              ) : (
                <RecordList<MapEntity>
                  actions={group && !isLassoMode && <GroupProps group={group} />}
                  idGetter={mapEntityIdGetter}
                  itemComponent={GroupRecord}
                  loading={loading || lassoLoading}
                  middleActions={<Frequency entityType={entityType} group={group} />}
                  onRangeChange={handleRangeChange}
                  onSearch={handleSearch}
                  range={range}
                  records={records}
                  searchActions={
                    !isLassoMode && (
                      <DataViewButton
                        entityCount={recordsCount}
                        loading={dataViewLoading}
                        onClick={handleDataViewClick}
                      />
                    )
                  }
                  searchPlaceholder={intl.formatMessage(messages.placeholder)}
                  textGetters={textGetters}
                  total={recordsCount}
                />
              )}
            </>
          ) : (
            <LoadingSpinner />
          )}
        </>
      )}
      {dataViewVisible && (
        <DataViewWrapper
          entityGroupIds={group?.id ? [group.id] : []}
          entityType={entityType}
          entityTypeRecordsCount={entityTypeRecordsCount}
          hideTabs
          onHide={hideDataView}
          totalCount={dataViewEntities.length}
        />
      )}
    </BaseSidebar>
  );
};

const mapStateToProps = (state: RootState) => ({
  dataViewEntities: getDataViewEntities(state),
  dataViewLoading: isDataViewLoading(state),
  entityType: getGroupEntityType(state),
  group: getGroup(state),
  isEntityViewVisible: isEntityViewVisible(state),
  lassoLoading: isLassoLoading(state),
  loading: areGroupRecordsLoading(state),
  multiPin: getMultiPin(state),
  records: getGroupRecords(state),
  recordsCount: getGroupRecordsTotalCount(state),
  tool: getMapViewTool(state),
  totalSelectedRecords: getRecordsListTotalBeforeFilter(state),
});

const mapDispatchToProps = {
  onExitGroupMode: exitGroupMode.request,
  onExitLassoMode: exitLassoMode,
  onFetchDataViewEntities: fetchDataViewEntities.request,
  onFetchRecords: fetchGroupRecords.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Sidebar);
